import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, CardBody, CardTitle, CardHeader, Button, Badge, Spinner } from
"reactstrap";
import { useAuth } from "../../contexts/AuthContext";
import { FileService, FolderService, UsageService, NotificationService } from "../../services/firestore";
import { formatFileSize, getFileIcon, getStatusBadge, timeAgo } from "../../services/demoData";
import { getCriticalProducts } from "../../services/stockAlertService";
import { ScmReportingService } from "../../services/enterpriseService";

import LabelledProgress from "../../components/LabelledProgress";
function getGreeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t("home.goodMorning");
  if (h < 18) return t("home.goodAfternoon");
  return t("home.goodEvening");
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [criticalStockCount, setCriticalStockCount] = useState(0);
  const [scmKpis, setScmKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const uid = user?.uid || "demo-user";
      const [f, fo, u, notifs, criticals, kpis] = await Promise.all([
      FileService.getFiles(uid),
      FolderService.getFolders(uid),
      UsageService.getUsage(uid),
      NotificationService.getNotifications(uid),
      getCriticalProducts(uid),
      ScmReportingService.getKpis().catch(() => null)]
      );
      setFiles(f);
      setFolders(fo);
      setUsage(u);
      setUnreadCount(notifs.filter((n) => !n.read).length);
      setCriticalStockCount(criticals.length);
      setScmKpis(kpis);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    setRefreshing(false);
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const recentFiles = files.slice(0, 5);
  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);
  const storageUsedGB = usage?.storageUsed || (totalSize / (1024 * 1024 * 1024)).toFixed(1);
  const storageLimitGB = usage?.storageLimit || 50;

  const storagePct = Math.min(100, Math.round((storageUsedGB / storageLimitGB) * 100) || 0);

  const stats = [
  { key: "folders", label: t("home.folders"), value: folders.length, icon: "ni ni-folder-17", tint: "warning", to: "/admin/folders" },
  { key: "files", label: t("home.files"), value: files.length, icon: "ni ni-single-copy-04", tint: "info", to: "/admin/files" },
  { key: "usage", label: t("home.gbUsage"), value: storageUsedGB, icon: "ni ni-chart-pie-35", tint: "success", to: "/admin/files" },
  { key: "notifs", label: t("home.notifications"), value: unreadCount, icon: "ni ni-bell-55", tint: "danger", to: "/admin/settings/notifications",
    hint: unreadCount > 0 ? `${unreadCount} ${t("dash.unread")}` : null }];


  const kpis = scmKpis ? [
  { label: t("dash.sales"), value: scmKpis.salesOrders, to: "/admin/sales/orders" },
  { label: t("dash.pos"), value: scmKpis.purchaseOrders, to: "/admin/procurement/purchase-orders" },
  { label: t("dash.mfg"), value: scmKpis.manufacturingOrders, to: "/admin/manufacturing/orders" },
  { label: t("dash.approvals"), value: scmKpis.pendingApprovals, to: "/admin/enterprise/approvals" },
  { label: t("dash.lowStock"), value: scmKpis.lowStock, to: "/admin/inventory/purchase-requests" },
  { label: t("dash.openRma"), value: scmKpis.openRmas, to: "/admin/after-sales/rma" }] :
  [];

  const actions = [
  { label: t("home.upload"), icon: "ni ni-cloud-upload-96", tint: "primary", to: "/admin/files/upload" },
  { label: t("home.foldersAction"), icon: "ni ni-folder-17", tint: "info", to: "/admin/folders" },
  { label: t("home.aiAssistant"), icon: "ni ni-chat-round", tint: "success", to: "/admin/ai-chat" },
  { label: t("home.notifications"), icon: "ni ni-bell-55", tint: "warning", to: "/admin/settings/notifications" },
  { label: t("criticalStock.viewAll"), icon: "ni ni-cart", tint: "danger", to: "/admin/inventory/purchase-requests", badge: criticalStockCount }];


  return (
    <>
      <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center mb-4">
              <Col>
                <h1 className="text-white mb-0">{getGreeting(t)}, {user?.displayName || t("home.defaultUser")} 👋</h1>
                <p className="text-white mt-2 mb-0">{t("dash.overview")}</p>
              </Col>
              <Col xs="12" md="auto" className="mt-3 mt-md-0">
                <Button color="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
                  <i className={`fas fa-sync-alt mr-1 ${refreshing ? "fa-spin" : ""}`} />
                  {refreshing ? t("common.loading") : t("common.refresh")}
                </Button>
                <Button color="primary" size="sm" onClick={() => navigate("/admin/files/upload")}>
                  <i className="ni ni-cloud-upload-96 mr-1" />{t("home.upload")}
                </Button>
              </Col>
            </Row>
            <Row>
              {stats.map((s) =>
              <Col lg="6" xl="3" key={s.key} className="mb-4">
                  <Card className="card-stats h-100" role="button" tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(s.to)}
                onKeyDown={(e) => {if (e.key === "Enter") navigate(s.to);}}>
                    <CardBody>
                      <Row className="align-items-center">
                        <div className="col">
                          <CardTitle tag="h5" className="text-uppercase text-muted mb-1">{s.label}</CardTitle>
                          <span className="h2 font-weight-bold mb-0">{loading ? "–" : s.value}</span>
                        </div>
                        <Col className="col-auto">
                          <div className={`icon icon-shape fd-tint-${s.tint}`}>
                            <i className={s.icon} />
                          </div>
                        </Col>
                      </Row>
                      {s.hint && <p className="mt-2 mb-0 text-muted text-sm">{s.hint}</p>}
                    </CardBody>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        {criticalStockCount > 0 &&
        <div className="fd-alert-banner mb-4" role="alert">
            <div className="fd-action__icon fd-tint-danger"><i className="fas fa-exclamation-triangle" /></div>
            <div className="fd-alert-banner__body">
              <h4 className="mb-0 text-danger">{t("criticalStock.title")}</h4>
              <span className="text-muted">
                <strong className="text-danger">{criticalStockCount}</strong> {t("criticalStock.products")}
              </span>
            </div>
            <Button color="danger" size="sm" onClick={() => navigate("/admin/inventory/purchase-requests")}>
              <i className="ni ni-cart mr-1" />{t("criticalStock.viewAll")}
            </Button>
          </div>
        }

        {kpis.length > 0 &&
        <Card className="mb-4">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">{t("dash.scmSnapshot")}</h3>
              <Button color="link" size="sm" onClick={() => navigate("/admin/enterprise/reports")}>
                {t("dash.fullReports")} <i className="fas fa-arrow-right ml-1" />
              </Button>
            </CardHeader>
            <CardBody>
              <Row>
                {kpis.map((k) =>
              <Col xl="2" md="4" xs="6" className="mb-3 mb-xl-0" key={k.label}>
                    <button type="button" className="fd-kpi w-100 border-0 text-left" onClick={() => navigate(k.to)}>
                      <small>{k.label}</small>
                      <strong>{k.value ?? 0}</strong>
                    </button>
                  </Col>
              )}
              </Row>
            </CardBody>
          </Card>
        }

        <Row>
          <Col xl="8" className="mb-4">
            <Card className="h-100">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{t("home.recentFiles")}</h3>
                <Button color="link" size="sm" onClick={() => navigate("/admin/files")}>
                  {t("home.seeAll")} <i className="fas fa-arrow-right ml-1" />
                </Button>
              </CardHeader>
              <CardBody className="p-0">
                {loading ?
                <div className="fd-loading"><Spinner color="primary" /><span>{t("common.loading")}</span></div> :
                recentFiles.length === 0 ?
                <div className="fd-empty">
                    <div className="fd-empty__icon"><i className="ni ni-cloud-upload-96" /></div>
                    <h4 className="fd-empty__title">{t("home.noFilesYet")}</h4>
                    <Button color="primary" size="sm" onClick={() => navigate("/admin/files/upload")}>{t("home.uploadFirstFile")}</Button>
                  </div> :

                <div className="table-responsive">
                    <table className="table align-items-center table-flush">
                      <thead className="thead-light">
                        <tr>
                          <th scope="col">{t("dash.colName")}</th>
                          <th scope="col">{t("dash.colSize")}</th>
                          <th scope="col">{t("dash.colStatus")}</th>
                          <th scope="col">{t("dash.colDate")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentFiles.map((file) => {
                        const badge = getStatusBadge(file.status);
                        return (
                          <tr key={file.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/files/${file.id}`)}>
                              <td className="font-weight-600">
                                <i className={`${getFileIcon(file.mimeType)} mr-2 text-primary`} />
                                {file.name}
                              </td>
                              <td>{formatFileSize(file.size)}</td>
                              <td><Badge color={badge.color}>{badge.label}</Badge></td>
                              <td><small className="text-muted">{timeAgo(file.createdAt)}</small></td>
                            </tr>);

                      })}
                      </tbody>
                    </table>
                  </div>
                }
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" className="mb-4">
            <Card className="mb-4">
              <CardHeader>
                <h3 className="mb-0">{t("home.quickActions")}</h3>
              </CardHeader>
              <CardBody>
                {actions.map((a) =>
                <button type="button" key={a.to + a.label} className="fd-action" onClick={() => navigate(a.to)}>
                    <span className={`fd-action__icon fd-tint-${a.tint}`}><i className={a.icon} /></span>
                    <span>{a.label}</span>
                    {a.badge > 0 ?
                  <Badge color="danger" pill>{a.badge}</Badge> :
                  <i className="fas fa-chevron-right fd-action__chev" />
                  }
                  </button>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <span className="fd-action__icon fd-tint-purple mr-3"><i className="ni ni-cloud-download-95" /></span>
                  <div>
                    <h5 className="text-uppercase text-muted mb-0">{t("home.cloudStorage")}</h5>
                    <span className="small">{t("home.storageUsed", { used: storageUsedGB, total: storageLimitGB })}</span>
                  </div>
                  <strong className="ml-auto h3 mb-0">{storagePct}%</strong>
                </div>
                <LabelledProgress label={t("a11y.progress.storage")} value={storagePct} color={storagePct > 80 ? "danger" : "primary"} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>);

}
